import React from "react";
import { Box, Chip, CircularProgress, Grid2, Typography } from "@mui/material";
import { BrandBatchSettings, Job, JobStatus, LanguageCode, User } from "../../../pages/playground/types";
import JobListView from "./job-list-view";
import JobListJobItem from "./job-item";
import BulkActionBar from "./bulk-action-bar";

export interface JobListContentProps {
  jobs: Job[];
  groupedJobs: Record<string, Job[]> | null;
  showBulkActions: boolean;
  selectedJobs: Set<string>;
  onJobSelectionChange: (jobId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  columnFilter: string | null;
  statusFilter: JobStatus | null;
  observerTarget: React.RefObject<HTMLDivElement>;
  isCompleted: boolean;
  isFetchingNextPage: boolean;
  onBulkStatusChange: (status: JobStatus) => void;
  onClearSelection: () => void;
  bulkLoading: boolean;
  hasGdriveFolder: boolean;
  user?: User;
  batchSettings?: BrandBatchSettings;
  laraLanguages?: LanguageCode[];
  laraInstructions?: string[];
  laraStyle?: string;
}

/**
 * Render jobs in grid or list view, with or without grouping
 */
export const JobListContent: React.FC<JobListContentProps> = ({
  jobs,
  groupedJobs,
  showBulkActions,
  selectedJobs,
  onJobSelectionChange,
  onSelectAll,
  columnFilter,
  statusFilter,
  observerTarget,
  isCompleted,
  isFetchingNextPage,
  onBulkStatusChange,
  onClearSelection,
  bulkLoading,
  hasGdriveFolder,
  user,
  batchSettings,
  laraLanguages,
  laraInstructions,
  laraStyle,
}) => {
  if (isCompleted && jobs.length === 0) {
    return (
      <Typography variant="body1" sx={{ textAlign: "center" }}>
        No jobs found with selected filters.
      </Typography>
    );
  }

  if (jobs.length <= 0) {
    return null;
  }

  return (
    <>
      {showBulkActions && (
        <BulkActionBar
          selectedJobsCount={selectedJobs.size}
          onStatusChange={onBulkStatusChange}
          onClear={onClearSelection}
          isLoading={bulkLoading}
        />
      )}

      {/* Show list view when bulk actions are enabled, grid view otherwise */}
      {showBulkActions ? (
        <JobListView
          jobs={jobs}
          groupedJobs={groupedJobs}
          showBulkActions={showBulkActions}
          selectedJobs={selectedJobs}
          onJobSelectionChange={onJobSelectionChange}
          onSelectAll={(selected) => onSelectAll(selected)}
        />
      ) : (
        /* Original grid view */
        <>
          {groupedJobs ? (
            Object.entries(groupedJobs).map(([groupName, groupJobs]) => (
              <Box key={groupName} sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {groupName}{" "}
                  <Chip label={groupJobs.length} size="small" sx={{ ml: 1 }} />
                </Typography>

                <Grid2
                  spacing={2}
                  container
                  alignItems="stretch"
                  sx={{ width: "100%" }}
                >
                  {groupJobs.map((job: Job, index: number) => {
                    return (
                      <JobListJobItem
                        key={index}
                        jobsLength={jobs?.length || 0}
                        job={job}
                        columnFilter={columnFilter}
                        statusFilter={statusFilter}
                        hasGdriveFolder={hasGdriveFolder}
                        user={user}
                        batchSettings={batchSettings}
                        laraLanguages={laraLanguages}
                        laraInstructions={laraInstructions}
                        laraStyle={laraStyle}
                      />
                    );
                  })}
                </Grid2>
              </Box>
            ))
          ) : (
            <Grid2
              spacing={2}
              container
              alignItems="stretch"
              sx={{ width: "100%" }}
            >
              {jobs.map((job: Job, index: number) => {
                return (
                  <JobListJobItem
                    key={index}
                    jobsLength={jobs?.length || 0}
                    job={job}
                    columnFilter={columnFilter}
                    statusFilter={statusFilter}
                    hasGdriveFolder={hasGdriveFolder}
                    user={user}
                    batchSettings={batchSettings}
                    laraLanguages={laraLanguages}
                    laraInstructions={laraInstructions}
                    laraStyle={laraStyle}
                  />
                );
              })}
            </Grid2>
          )}
        </>
      )}

      {/* Observer target with more visible height for reliable detection */}
      <div ref={observerTarget} style={{ height: "40px", width: "100%" }} />

      {/* Loading indicator with text for better visibility */}
      {isFetchingNextPage && (
        <Box
          sx={{
            textAlign: "center",
            my: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      )}
    </>
  );
};
